import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  ArrowLeft,
  FloppyDisk,
  SquaresFourIcon,
  XCircle,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../../shared/ui/icons";
import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../../components/useBreadcrumbBack";
import { useAuth } from "../../../../providers/AuthProvider";
import { useToast } from "../../../../providers/ToastProvider";
import { ApiError } from "../../../../services/apiClient";
import {
  createControlPoint,
  getControlPoint,
  updateControlPoint,
  type ControlPoint,
} from "../../../shared/api/controlPoints";
import { ControlPointMap } from "../../components/ControlPointMap";

import { PageShell } from "../../../shared/layouts/PageShell";
import { PageHeader } from "../../../shared/ui/layouts/PageHeader";
import { FormFieldLabel } from "../../../../components/FormFieldLabel";
import { GeoLocationSelect, type GeoSelection } from "../../../../components/geo/GeoLocationSelect";
import { ConfirmDialog } from "../../../../components/ConfirmDialog";

type RouteState = {
  controlPoint?: ControlPoint;
};

type DMSState = {
  degrees: string;
  minutes: string;
  seconds: string;
  hemisphere: "N" | "S" | "E" | "W";
};

type FormState = {
  code: string;
  name: string;
  regionId: string;
  provinceId: string;
  municipalityId: string;
  srid: string;
  projectedSrid: string;
  projectedEasting: string;
  projectedNorthing: string;
  latitudeDms: DMSState;
  longitudeDms: DMSState;
  source: string;
  accuracy: string;
};

const defaultLatDms: DMSState = { degrees: "", minutes: "", seconds: "", hemisphere: "N" };
const defaultLonDms: DMSState = { degrees: "", minutes: "", seconds: "", hemisphere: "E" };

const createEmptyForm = (): FormState => ({
  code: "",
  name: "",
  regionId: "",
  provinceId: "",
  municipalityId: "",
  srid: "4326",
  projectedSrid: "3123",
  projectedEasting: "",
  projectedNorthing: "",
  latitudeDms: defaultLatDms,
  longitudeDms: defaultLonDms,
  source: "",
  accuracy: "",
});

const decimalToDms = (value?: number, type: "lat" | "lon" = "lat"): DMSState => {
  if (value == null || Number.isNaN(value)) {
    return type === "lat" ? { ...defaultLatDms } : { ...defaultLonDms };
  }
  const hemisphere: "N" | "S" | "E" | "W" =
    type === "lat" ? (value >= 0 ? "N" : "S") : value >= 0 ? "E" : "W";
  const absValue = Math.abs(value);
  const degrees = Math.floor(absValue);
  const minutesFloat = (absValue - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  const seconds = (minutesFloat - minutes) * 60;
  return {
    degrees: String(degrees),
    minutes: String(minutes),
    seconds: seconds ? seconds.toFixed(3) : "",
    hemisphere,
  };
};

const controlPointToForm = (controlPoint: ControlPoint): FormState => ({
  code: controlPoint.code ?? "",
  name: controlPoint.name ?? "",
  regionId: controlPoint.region_id ?? "",
  provinceId: controlPoint.province_id ?? "",
  municipalityId: controlPoint.municipality_id ?? "",
  srid: String(controlPoint.srid ?? ""),
  projectedSrid: String(controlPoint.projected_srid ?? "3123"),
  projectedEasting: controlPoint.projected_easting != null ? String(controlPoint.projected_easting) : "",
  projectedNorthing: controlPoint.projected_northing != null ? String(controlPoint.projected_northing) : "",
  latitudeDms: decimalToDms(controlPoint.latitude, "lat"),
  longitudeDms: decimalToDms(controlPoint.longitude, "lon"),
  source: controlPoint.source ?? "",
  accuracy:
    controlPoint.accuracy_m != null ? String(controlPoint.accuracy_m) : "",
});

const parseDms = (dms: DMSState): number | null => {
  const degrees = Number(dms.degrees);
  const minutes = Number(dms.minutes);
  const seconds = Number(dms.seconds);
  if (
    Number.isNaN(degrees) ||
    Number.isNaN(minutes) ||
    Number.isNaN(seconds)
  ) {
    return null;
  }
  const decimal = degrees + minutes / 60 + seconds / 3600;
  const value =
    dms.hemisphere === "S" || dms.hemisphere === "W" ? -decimal : decimal;
  return value;
};

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const cardClasses = "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col gap-6 dark:border-slate-800 dark:bg-slate-900";
const formClasses = "flex flex-col gap-8";
const gridClasses = "grid grid-cols-1 gap-6 sm:grid-cols-2";
const fieldClasses = "flex flex-col gap-2";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";
const actionsClasses = "flex items-center justify-end gap-3";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const primaryButtonClasses = "pm-btn pm-btn-primary";

export function AdminTiePointFormPage() {
  const { t } = useTranslation();
  const { controlPointId } = useParams<{ controlPointId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const toast = useToast();

  const isEditing = Boolean(controlPointId);

  const [form, setForm] = useState<FormState>(createEmptyForm);
  const [isLoading, setIsLoading] = useState<boolean>(isEditing);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setIsLoading(false);
      setForm((prev) => ({ ...prev, srid: "4326", projectedSrid: "3123" }));
      return;
    }

    if (!accessToken) {
      return;
    }

    if (!controlPointId) {
      setError(t("admin.tie_points.not_found"));
      setIsLoading(false);
      return;
    }

    const state = location.state as RouteState | undefined;
    if (state?.controlPoint && state.controlPoint.id === controlPointId) {
      setForm(controlPointToForm(state.controlPoint));
      setIsLoading(false);
      return;
    }

    const loadControlPoint = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const target = await getControlPoint(accessToken, controlPointId);
        setForm(controlPointToForm(target));
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : t("admin.tie_points.form.loading");
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    void loadControlPoint();
  }, [isEditing, controlPointId, location.state, accessToken, t]);

  const renderDmsBlock = (
    label: string,
    stateKey: "latitudeDms" | "longitudeDms",
    hintKey: string,
    previewLabel: string,
    previewValue: number | null
  ) => (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_240px]">
      <label className={fieldClasses}>
        <FormFieldLabel label={label} />
        <div className="flex gap-2">
          <input
            type="number"
            inputMode="decimal"
            className={inputClasses}
            placeholder={t("admin.tie_points.form.degrees_placeholder")}
            value={form[stateKey].degrees}
            onChange={handleDmsChange(stateKey, "degrees")}
          />
          <input
            type="number"
            inputMode="decimal"
            className={inputClasses}
            placeholder={t("admin.tie_points.form.minutes_placeholder")}
            value={form[stateKey].minutes}
            onChange={handleDmsChange(stateKey, "minutes")}
          />
          <input
            type="number"
            inputMode="decimal"
            className={inputClasses}
            placeholder={t("admin.tie_points.form.seconds_placeholder")}
            value={form[stateKey].seconds}
            onChange={handleDmsChange(stateKey, "seconds")}
          />
          <select
            className={inputClasses}
            style={{ width: "auto" }}
            value={form[stateKey].hemisphere}
            onChange={handleDmsChange(stateKey, "hemisphere")}
          >
            {stateKey === "latitudeDms" ? (
              <>
                <option value="N">N</option>
                <option value="S">S</option>
              </>
            ) : (
              <>
                <option value="E">E</option>
                <option value="W">W</option>
              </>
            )}
          </select>
        </div>
        <small className="text-xs text-slate-500 dark:text-slate-400">
          {t(hintKey)}
        </small>
      </label>
      <label className={fieldClasses}>
        <FormFieldLabel label={previewLabel} />
        <input
          type="text"
          className={inputClasses}
          value={
            previewValue != null
              ? previewValue.toFixed(8)
              : t("admin.tie_points.form.preview_pending")
          }
          readOnly
        />
      </label>
    </div>
  );

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    const pageTitle = isEditing ? t("admin.tie_points.edit.title") : t("admin.tie_points.add.title");
    return [
      { label: t("admin.title"), to: "/admin" },
      { label: t("admin.tie_points.list_title"), to: "/admin/tie-points" },
      { label: pageTitle },
    ];
  }, [isEditing, t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/admin/tie-points" },
  });

  if (!user || !accessToken) {
    return null;
  }

  if (user.system_role !== "admin") {
    return (
      <div className={pageStackClasses}>
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={t("common.access_denied.title")}
          subtitle={t("common.access_denied.message")}
          actions={
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={() => navigate("/dashboard")}
            >
              <SquaresFourIcon {...defaultIconProps} aria-hidden />
              {t("navigation.to_dashboard")}
            </button>
          }
        />
      </div>
    );
  }

  const handleChange =
    (field: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleDmsChange =
    (field: "latitudeDms" | "longitudeDms", key: keyof DMSState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = event.target.value;
      setForm((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          [key]: value,
        },
      }));
    };

  const latitudeDecimal = parseDms(form.latitudeDms);
  const longitudeDecimal = parseDms(form.longitudeDms);
  const handleGeoSelectionChange = (selection: GeoSelection) => {
    setForm((prev) => ({
      ...prev,
      regionId: selection.regionId ?? "",
      provinceId: selection.provinceId ?? "",
      municipalityId: selection.municipalityId ?? "",
    }));
  };
  const mapLabel = (form.name?.trim() || form.code)?.trim() || undefined;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken) {
      return;
    }

    const srid = Number(form.srid);
    const projectedSrid = Number(form.projectedSrid);
    const projectedEasting = Number(form.projectedEasting);
    const projectedNorthing = Number(form.projectedNorthing);
    const accuracy =
      form.accuracy.trim() === "" ? null : Number(form.accuracy);
    const provinceId = form.provinceId || undefined;
    const municipalityId = form.municipalityId || undefined;

    if (
      Number.isNaN(srid) ||
      Number.isNaN(projectedSrid) ||
      Number.isNaN(projectedEasting) ||
      Number.isNaN(projectedNorthing) ||
      (accuracy !== null && Number.isNaN(accuracy))
    ) {
      setError(t("admin.tie_points.form.invalid_numbers"));
      return;
    }

    if (!form.code.trim()) {
      setError(t("admin.tie_points.form.code_required"));
      return;
    }

    const formatDmsString = (dms: DMSState): string | undefined => {
      if (
        !dms.degrees.trim() ||
        !dms.minutes.trim() ||
        !dms.seconds.trim()
      ) {
        return undefined;
      }
      return `${dms.degrees}°${dms.minutes}'${dms.seconds}" ${dms.hemisphere}`;
    };

    const payload = {
      code: form.code.trim(),
      name: form.name.trim() || undefined,
      srid,
      province_id: provinceId,
      municipality_id: municipalityId,
      latitude_dms: formatDmsString(form.latitudeDms),
      longitude_dms: formatDmsString(form.longitudeDms),
      projected_srid: projectedSrid,
      projected_easting: projectedEasting,
      projected_northing: projectedNorthing,
      source: form.source.trim() || undefined,
      accuracy_m: accuracy ?? undefined,
    };

    setIsSubmitting(true);
    setError(null);
    try {
      if (isEditing && controlPointId) {
        await updateControlPoint(accessToken, controlPointId, payload);
        toast.success(t("admin.tie_points.form.update_success"));
      } else {
        await createControlPoint(accessToken, payload);
        toast.success(t("admin.tie_points.form.create_success"));
      }
      navigate("/admin/tie-points", { replace: true, state: { refreshed: true } });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : isEditing
            ? t("admin.tie_points.form.update_error")
            : t("admin.tie_points.form.create_error");
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowDiscardConfirm(true);
  };

  const pageTitle = isEditing ? t("admin.tie_points.edit.title") : t("admin.tie_points.add.title");

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />

      <PageHeader
        title={pageTitle}
        subtitle={
          isEditing
            ? t("admin.tie_points.edit.subtitle")
            : t("admin.tie_points.add.subtitle")
        }
        actions={
          <button
            type="button"
            className={ghostButtonClasses}
            onClick={handleBack}
          >
            <ArrowLeft {...defaultIconProps} aria-hidden />
            {t("actions.back")}
          </button>
        }
      />

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
          {error}
        </div>
      )}

      {isLoading ? (
        <p className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">{t("admin.tie_points.form.loading")}</p>
      ) : (
        <form className={formClasses} onSubmit={handleSubmit}>
          <section className={cardClasses}>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("admin.tie_points.form.section_reference")}</h2>
            <div className={gridClasses}>
              <label className={fieldClasses}>
                <FormFieldLabel label={t("admin.tie_points.form.code_label")} />
                <input
                  type="text"
                  className={inputClasses}
                  value={form.code}
                  onChange={handleChange("code")}
                  placeholder={t("admin.tie_points.form.code_placeholder")}
                  required
                />
              </label>
              <label className={fieldClasses}>
                <FormFieldLabel label={t("admin.tie_points.form.name_label")} optional />
                <input
                  type="text"
                  className={inputClasses}
                  value={form.name}
                  onChange={handleChange("name")}
                  placeholder={t("admin.tie_points.form.name_placeholder")}
                />
              </label>
              <label className={fieldClasses}>
                <FormFieldLabel label={t("common.srid")} />
                <input
                  type="number"
                  inputMode="numeric"
                  className={inputClasses}
                  value={form.srid}
                  onChange={handleChange("srid")}
                  required
                />
              </label>
            </div>
          </section>

          <section className={cardClasses}>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("admin.tie_points.form.section_location")}</h2>
            <GeoLocationSelect
              value={{
                regionId: form.regionId || undefined,
                provinceId: form.provinceId || undefined,
                municipalityId: form.municipalityId || undefined,
              }}
              onChange={handleGeoSelectionChange}
            />
            <small className="text-xs text-slate-500 dark:text-slate-400">
              {t("admin.tie_points.form.location_hint")}
            </small>
          </section>

          <section className={cardClasses}>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("admin.tie_points.form.section_geographic")}</h2>
            {renderDmsBlock(
              t("common.latitude"),
              "latitudeDms",
              "admin.tie_points.form.latitude_dms_hint",
              t("admin.tie_points.form.preview_latitude"),
              latitudeDecimal
            )}
            {renderDmsBlock(
              t("common.longitude"),
              "longitudeDms",
              "admin.tie_points.form.longitude_dms_hint",
              t("admin.tie_points.form.preview_longitude"),
              longitudeDecimal
            )}
          </section>

          <section className={cardClasses}>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("admin.tie_points.form.section_plane")}</h2>
            <div className={gridClasses}>
              <label className={fieldClasses}>
                <FormFieldLabel label={t("admin.tie_points.form.projected_srid_label")} />
                <input
                  type="number"
                  inputMode="numeric"
                  className={inputClasses}
                  value={form.projectedSrid}
                  onChange={handleChange("projectedSrid")}
                  required
                />
              </label>
              <label className={fieldClasses}>
                <FormFieldLabel label={t("admin.tie_points.form.easting_label")} />
                <input
                  type="number"
                  inputMode="decimal"
                  className={inputClasses}
                  value={form.projectedEasting}
                  onChange={handleChange("projectedEasting")}
                  required
                />
              </label>
              <label className={fieldClasses}>
                <FormFieldLabel label={t("admin.tie_points.form.northing_label")} />
                <input
                  type="number"
                  inputMode="decimal"
                  className={inputClasses}
                  value={form.projectedNorthing}
                  onChange={handleChange("projectedNorthing")}
                  required
                />
              </label>
              <small className="text-xs text-slate-500 col-span-full dark:text-slate-400">
                {t("admin.tie_points.form.projected_hint")}
              </small>
            </div>
          </section>

          <section className={cardClasses}>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("admin.tie_points.form.section_map")}</h2>
            <ControlPointMap
              latitude={latitudeDecimal}
              longitude={longitudeDecimal}
              label={mapLabel}
              isLoading={isLoading}
              loadingMessage={t("admin.tie_points.form.map_loading_state")}
              emptyMessage={t("admin.tie_points.form.map_empty_state")}
              panelTitle={t("admin.tie_points.form.map_panel_title")}
            />
          </section>

          <section className={cardClasses}>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t("admin.tie_points.form.section_metadata")}</h2>
            <div className={gridClasses}>
              <label className={fieldClasses}>
                <FormFieldLabel label={t("admin.tie_points.form.source_label")} optional />
                <input
                  type="text"
                  className={inputClasses}
                  value={form.source}
                  onChange={handleChange("source")}
                  placeholder={t("admin.tie_points.form.source_placeholder")}
                />
              </label>
              <label className={fieldClasses}>
                <FormFieldLabel label={t("admin.tie_points.form.accuracy_label")} optional />
                <input
                  type="number"
                  inputMode="decimal"
                  className={inputClasses}
                  value={form.accuracy}
                  onChange={handleChange("accuracy")}
                  min="0"
                  step="0.01"
                />
              </label>
            </div>
          </section>
          <div className={actionsClasses}>
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleCancel}
            >
              <XCircle {...defaultIconProps} aria-hidden />
              {t("actions.cancel")}
            </button>
            <button
              type="submit"
              className={primaryButtonClasses}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                t("actions.saving")
              ) : (
                <>
                  <FloppyDisk {...defaultIconProps} aria-hidden />
                  {isEditing ? t("actions.save_changes") : t("admin.tie_points.add.button")}
                </>
              )}
            </button>
          </div>
        </form>
      )}
      <ConfirmDialog
        open={showDiscardConfirm}
        title={t("common.confirm_discard")}
        confirmLabel={t("actions.confirm", "Confirm")}
        cancelLabel={t("actions.cancel")}
        confirmTone="danger"
        onConfirm={() => {
          handleBack();
          setShowDiscardConfirm(false);
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </PageShell>
  );
}
